import { Config, Template } from '../types/index.js';
import { getConfigPath, readJson, writeJson } from '../utils/file.js';
import { DEFAULT_CONFIG } from '../config/defaults.js';

/**
 * 配置管理器
 */
export class ConfigManager {
  private config: Config | null = null;

  /**
   * 加载配置
   */
  async load(): Promise<Config> {
    if (this.config) {
      return this.config;
    }

    const configPath = getConfigPath();
    const loadedConfig = await readJson<Config>(configPath);

    this.config = loadedConfig
      ? { ...DEFAULT_CONFIG, ...loadedConfig }
      : { ...DEFAULT_CONFIG };

    return this.config;
  }

  /**
   * 保存配置
   */
  async save(config?: Config): Promise<void> {
    const configToSave = config || this.config;
    if (!configToSave) {
      throw new Error('没有可保存的配置');
    }

    const configPath = getConfigPath();
    await writeJson(configPath, configToSave);
    this.config = configToSave;
  }

  /**
   * 获取所有模板
   */
  async getTemplates(): Promise<Record<string, Template>> {
    const config = await this.load();
    return config.templates || {};
  }

  /**
   * 获取单个模板
   */
  async getTemplate(name: string): Promise<Template | null> {
    const templates = await this.getTemplates();
    return templates[name] || null;
  }

  /**
   * 添加模板
   */
  async addTemplate(template: Template): Promise<void> {
    const config = await this.load();
    config.templates[template.name] = template;
    await this.save(config);
  }

  /**
   * 删除模板
   */
  async removeTemplate(name: string): Promise<void> {
    const config = await this.load();
    delete config.templates[name];
    await this.save(config);
  }

  /**
   * 更新模板使用时间
   */
  async updateTemplateLastUsed(name: string): Promise<void> {
    const config = await this.load();
    if (config.templates[name]) {
      config.templates[name].lastUsed = new Date().toISOString();
      await this.save(config);
    }
  }
}

export const configManager = new ConfigManager();
